import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, formatApiError } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = not-authenticated, object = authenticated
  const [user, setUser] = useState(null);

  const checkSession = useCallback(async () => {
    try {
      const res = await api.get('/api/auth/me');
      setUser(res.data);
    } catch (_) {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = async (email, password) => {
    try {
      const res = await api.post('/api/auth/login', { email, password });
      // 2FA gate: backend returns twofa_required + a pending token instead
      // of a session. Surface it so the login UI can show the code step.
      if (res.data?.twofa_required) {
        return { success: false, twofaRequired: true, pendingToken: res.data.pending_token };
      }
      setUser(res.data.user);
      return { success: true };
    } catch (err) {
      return { success: false, error: formatApiError(err) };
    }
  };

  // Complete a login that returned twofaRequired.
  const loginTwoFa = async (pendingToken, code) => {
    try {
      const res = await api.post('/api/auth/2fa/login', { pending_token: pendingToken, code });
      setUser(res.data.user);
      return { success: true, ...res.data };
    } catch (err) {
      return { success: false, error: formatApiError(err) };
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (_) {
      /* noop */
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, loginTwoFa, logout, refresh: checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
